/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import i18n from '../../../i18n/AdHocBundle.properties';
import {
    Aggregation,
    Field,
    Formats,
    IMetadataComponent,
    MetadataQuery,
    MetadataQueryGroupByItem,
    PresentationElement,
    PresentationGroup,
    Schema
} from '../../store/types/MetadataTypes';
import { fromNullable, Nullable } from '../../store/types/CommonTypes';
import { IAxisItem, ILevelsLayout, TAxis } from '../../store/types/StateTypes';
import levelsLayoutHelper from '../../store/util/levelsLayoutHelper';
import TypeKindMap from '../../enums/TypeKindMap';
import DefaultFormatBundle from '../../enums/DefaultFormatBundle';
import DefaultFormatMap from '../../enums/DefaultFormatMap';

interface Bundle {
    [key: string]: string
}

function extractLabel(levelMetadata: any, bundle: Bundle) {
    const { labelId } = levelMetadata;
    let { label } = levelMetadata;

    if (labelId && bundle) {
        label = bundle[labelId];
    }

    return label || labelId;
}

function extractLabelWithAggregation(agg: any, levelMetadata: any, bundle: Bundle) {
    const { labelId, aggregation } = levelMetadata;
    const { functionName } = agg;
    let { label } = levelMetadata;

    if (labelId && bundle) {
        label = bundle[labelId] || label || labelId;
    }

    if (functionName && (functionName !== aggregation)) {
        label = `${label} (${i18n[`adhoc.function.aggregate.name.${functionName}`]})`;
    }

    return label;
}

const findComponentDeep: any = (componentSet: Array<any>, componentType: string) => {
    const components = componentSet.filter((type: any) => type.componentType === componentType);
    const childComponents: any = components.reduce((res, model) => {
        return res.concat(findComponentDeep(model.components, componentType));
    }, []);
    return components.concat(childComponents);
};

const getColumnsComponent = (components: Array<any>) => {
    return components.find((component) => component.properties.name === 'columns' && component.componentType === 'axis');
};

const getRowsComponent = (components: Array<any>) => {
    return components.find((component) => component.properties.name === 'rows' && component.componentType === 'axis');
};

const getMultiAxisComponent = (components: any) => {
    return findComponentDeep(components.components, 'crosstab')[0] || findComponentDeep(components.components, 'chart')[0];
};

const getMeasuresComponent = (components: any) => {
    return findComponentDeep(components.components, 'measures')[0];
};

const getSchemaEntry = (presentation: (PresentationGroup | PresentationElement)[], fieldName: string): PresentationElement => {
    let element: Nullable<PresentationElement> = null;

    for (let i = presentation.length - 1; i > -1 && !element; i -= 1) {
        if ((presentation[i] as any).group) {
            element = getSchemaEntry((presentation[i] as any).group.elements, fieldName);
        } else if ((presentation[i] as any).element) {
            if ((presentation[i] as any).element.hierarchicalName === fieldName) {
                ({ element } = (presentation[i]) as any);
            }
        } else {
            throw new Error('Unknown element type in schema');
        }
    }
    return element as PresentationElement;
};

const extractMeasures = (memo: Array<any>, measures: any) => {
    return measures.components.reduce((mem: any, measure: any) => {
        const memObj = mem;
        memObj[measure.properties.reference] = {
            aggregationFormat: measure.properties.format,
            aggregationFormatId: measure.properties.formatId
        };
        return memObj;
    }, memo);
};

const extractFormats = (axis: any, measures: any) => {
    return axis.components.reduce((memo: any, levelComp: any) => {
        let memoObj = memo;
        if (levelComp.properties.reference === 'Measures') {
            memoObj = extractMeasures(memo, measures);
        } else {
            memoObj[levelComp.properties.reference] = {
                format: levelComp.properties.format,
                formatId: levelComp.properties.formatId,
                aggregationFormat: levelComp.properties.aggregationFormat,
                aggregationFormatId: levelComp.properties.aggregationFormatId
            };
        }
        return memoObj;
    }, {});
};

const hasSummary = (axis: any, id: string) => {
    const component = axis.components.find((levelComp: any) => (levelComp.properties.reference === id));
    return component && component.properties.includeAll;
};

const readSelectField = (field: Field, schema: Schema, formats: Formats, bundle: Bundle): IAxisItem => {
    const levelMetadata = getSchemaEntry(schema.presentation, field.field);
    return {
        id: field.id || field.field,
        kind: levelMetadata.kind,
        field: levelMetadata.name,
        dimension: levelMetadata.hierarchicalName,
        hierarchicalName: levelMetadata.hierarchicalName,
        aggregationFunctionName: levelMetadata.aggregation,
        defaultAggregationFunction: levelMetadata.aggregation,
        aggregationFormat: formats[field.id || field.field].aggregationFormat,
        aggregationFormatId: formats[field.id || field.field].aggregationFormatId,
        format: formats[field.id || field.field].format,
        formatId: formats[field.id || field.field].formatId,
        type: levelMetadata.type,
        aggregationType: levelsLayoutHelper.aggregationType(levelMetadata.type, levelMetadata.aggregation),
        label: extractLabel(levelMetadata, bundle),
        labelId: levelMetadata.labelId,
        includeAll: true,
        categorizer: levelsLayoutHelper.defaultCategorizer(((levelMetadata as unknown) as IAxisItem))
    };
};

const readSelectAggregation = (aggregation: any, id: any, levelMetadata: any, formats: any, bundle: Bundle) => {
    return {
        field: levelMetadata.hierarchicalName,
        aggregationFunctionName: aggregation.functionName || levelMetadata.aggregation,
        aggregationType: aggregation.type,
        aggregationFormat: formats[aggregation.id || aggregation.fieldRef].aggregationFormat,
        aggregationFormatId: formats[aggregation.id || aggregation.fieldRef].aggregationFormatId,
        defaultAggregationFunction: levelMetadata.aggregation,
        format: formats[aggregation.id || aggregation.fieldRef].format,
        formatId: formats[aggregation.id || aggregation.fieldRef].formatId,
        id: id || aggregation.id || levelMetadata.hierarchicalName,
        firstLevelExpression: aggregation.firstLevelExpression,
        timeBalanceFunctionName: aggregation.timeBalanceFunctionName,
        kind: levelMetadata.kind,
        dimension: levelMetadata.hierarchicalName,
        hierarchicalName: levelMetadata.hierarchicalName,
        type: levelMetadata.type,
        label: extractLabelWithAggregation(aggregation, levelMetadata, bundle),
        labelId: levelMetadata.labelId,
        includeAll: true
    }
};

const readSelectAggregations = (aggregations: Aggregation[], schema: Schema, columns: any, fields: Field[], bundle: Bundle) => {
    return aggregations.map((agg) => {
        let ref: string,
            id: Nullable<string> = null;
        if (fields) {
            const foundField = fields.find((field) => agg.fieldRef === field.id || agg.fieldRef === field.field);
            if (foundField) {
                ref = foundField.field;
                id = foundField.id || foundField.field;
            } else {
                ref = agg.fieldRef;
            }
        } else {
            ref = agg.fieldRef;
        }
        return readSelectAggregation(agg, id, getSchemaEntry(schema.presentation, ref), columns, bundle);
    });
};

const readMultiAxisItems: any = (items: MetadataQueryGroupByItem[], schema: any, axisComponent: any, measuresComponent: any, aggregations: any, bundle: Bundle): TAxis => {
    const formats = extractFormats(axisComponent, measuresComponent);
    let memoObj: TAxis = [] as any;
    return items.reduce((memo: TAxis, item: MetadataQueryGroupByItem) => {
        memoObj = memo;
        if (item.level) {
            const levelMetadata = getSchemaEntry(schema.presentation, item.level.field);
            const id = item.level.id || levelMetadata.hierarchicalName;
            const obj: IAxisItem = {
                id,
                kind: levelMetadata.kind,
                field: levelMetadata.name,
                dimension: levelMetadata.hierarchicalName,
                hierarchicalName: levelMetadata.hierarchicalName,
                aggregationFunctionName: levelMetadata.aggregation,
                aggregationType: levelsLayoutHelper.aggregationType(levelMetadata.type, levelMetadata.aggregation),
                defaultAggregationFunction: levelMetadata.aggregation,
                type: levelMetadata.type,
                label: extractLabel(levelMetadata, bundle),
                labelId: levelMetadata.labelId,
                includeAll: hasSummary(axisComponent, id),
                format: formats[item.level.id || item.level.field].format,
                formatId: formats[item.level.id || item.level.field].formatId,
                aggregationFormat: formats[item.level.id || item.level.field].aggregationFormat,
                aggregationFormatId: formats[item.level.id || item.level.field].aggregationFormatId,
                categorizer: item.level.categorizer
            };
            memoObj.push(obj);
        } else if (item.aggregations) {
            memoObj = memo.concat(readSelectAggregations(aggregations, schema, formats, [], bundle) as any);
        } else {
            throw new Error('Unknown element in "GroupBy"');
        }
        return memoObj;
    }, []);
};

const readMultiLevelGroups: any = (items: Array<any>, schema: any, component: any, bundle: Bundle) => {
    const tableGroupComponent = component.components.find((table: any) => table.componentType === 'table').components.filter((column: any) => column.componentType === 'group');
    const formats = tableGroupComponent.reduce((memo: any, column: any) => {
        const memoObj = memo;
        memoObj[column.properties.reference] = {
            aggregationFormat: column.properties.aggregationFormat,
            format: column.properties.format
        };
        return memoObj;
    }, {});
    const groups = (((val, key) => (val.map((o) => o[key])))(items, 'group'))
        .filter((item) => item);
    return groups.map((group) => {
        const entry = getSchemaEntry(schema.presentation, group.field);
        const reference = group.id || group.field;
        return {
            id: group.id || entry.hierarchicalName,
            kind: entry.kind,
            field: entry.name,
            dimension: entry.hierarchicalName,
            hierarchicalName: entry.hierarchicalName,
            aggregationFunctionName: entry.aggregation,
            defaultAggregationFunction: entry.aggregation,
            type: entry.type,
            label: extractLabel(entry, bundle),
            labelId: entry.labelId,
            aggregationFormat: formats[reference].aggregationFormat,
            aggregationFormatId: formats[reference].aggregationFormatId,
            format: formats[reference].format,
            formatId: formats[reference].formatId,
            includeAll: true
        };
    });
};

type TItems = {
    aggregations: Aggregation[],
    fields?: Field[], distinctFields?: Field[]
};

type TComponent = { components: any[] };

const readMultiLevelColumns = (items: TItems, schema: Schema, component: TComponent, bundle: Bundle) => {
    const tableColumnComponent = component.components
        .find((table: { componentType: string }) => table.componentType === 'table').components
        .filter((column: { componentType: string }) => column.componentType === 'column');

    const res = [],
        aggregations: Nullable<Aggregation>[] = items.aggregations || [],
        fields = items.fields || items.distinctFields || [],
        formats = tableColumnComponent.reduce((memo: Formats, column: IMetadataComponent) => {
            const memoObj = memo;
            memoObj[column.properties.reference] = {
                aggregationFormat: column.properties.aggregationFormat || '',
                aggregationFormatId: column.properties.aggregationFormatId || '',
                format: column.properties.format || '',
                formatId: column.properties.formatId || ''
            };
            return memoObj;
        }, {});

    for (let i = 0; i < fields.length; i += 1) {
        let item: Nullable<any>;
        const field = fields[i];

        for (let j = 0; j < aggregations.length && !item; j += 1) {
            const aggregation = aggregations[j];
            if (aggregation && (aggregation.fieldRef === field.id || aggregation.fieldRef === field.field)) {
                item = readSelectAggregation(aggregation, field.id || field.field, getSchemaEntry(schema.presentation, field.field), formats, bundle);
                aggregations[j] = null;
            }
        }

        if (item) {
            res.push(item);
        } else {
            res.push(readSelectField(fields[i], schema, formats, bundle));
        }
    }

    const otherAggregations = aggregations
        .filter((item) => item !== null)
        .map((item) => fromNullable(item));

    return res.concat(readSelectAggregations(otherAggregations, schema, formats, fields, bundle));
};

function setDefaults(axis: TAxis): TAxis {
    return axis.map((it) => {
        const res = it;
        if (it.kind !== 'measures') {
            const level = it as IAxisItem;
            if (levelsLayoutHelper.isDateTime(level)) {
                if (!level.categorizer) {
                    level.categorizer = levelsLayoutHelper.defaultCategorizer(level);
                }
            } else if (level.categorizer === 'all') {
                level.categorizer = undefined;
            }

            if (!level.format) {
                if (level.aggregationFormat && TypeKindMap[level.aggregationType] === TypeKindMap[level.type]) {
                    level.format = level.aggregationFormat;
                    level.formatId = level.aggregationFormatId;
                } else {
                    level.formatId = DefaultFormatMap[level.type];
                    // @ts-ignore
                    level.format = DefaultFormatBundle[level.formatId];
                }
            }

            if (!level.aggregationFormat) {
                if (TypeKindMap[level.aggregationType] === TypeKindMap[level.type]) {
                    level.aggregationFormat = level.format;
                    level.aggregationFormatId = level.formatId;
                } else {
                    // @ts-ignore
                    level.aggregationFormat = DefaultFormatBundle[DefaultFormatMap[level.type]];
                    level.aggregationFormatId = DefaultFormatMap[level.type];
                }
            }
        }

        return res;
    });
}

function generateId(base: string, idSet: any, depth: number): string {
    let id = [base, '_', depth].join('');
    if (idSet[id]) {
        id = generateId(base, idSet, depth + 1);
    }

    return id;
}

function verifyIds(layout:ILevelsLayout): ILevelsLayout {
    const idSet: any = {};

    layout.columns.concat(layout.rows).forEach((item) => {
        if (item.kind === 'level') {
            const level = item as IAxisItem;

            if (!level.id) {
                level.id = level.field;
            }

            if (idSet[level.id]) {
                level.id = generateId(level.field, idSet, 1);
            }

            idSet[level.id] = true;
        }
    });

    return layout;
}

const getLevelsLayout = (metaObj: any): ILevelsLayout => {
    let query = {} as MetadataQuery,
        levelLayout = {} as ILevelsLayout;

    if (metaObj.query.multiAxis) {
        query = metaObj.query.multiAxis;
    } else if (metaObj.query.multiLevel) {
        query = metaObj.query.multiLevel;
    }

    const schemaObj = metaObj.schema;
    if (query.groupBy) {
        if (query.groupBy.rows && query.groupBy.columns) {
            const component = getMultiAxisComponent(metaObj.component),
                measures = getMeasuresComponent(component);
            levelLayout = {
                rows: readMultiAxisItems(query.groupBy.rows.items, schemaObj, getRowsComponent(component.components), measures, query.select.aggregations, metaObj.bundle),
                columns: readMultiAxisItems(query.groupBy.columns.items, schemaObj, getColumnsComponent(component.components), measures, query.select.aggregations, metaObj.bundle)
            }
        } else {
            levelLayout = {
                rows: readMultiLevelGroups(query.groupBy, schemaObj, metaObj.component, metaObj.bundle),
                columns: readMultiLevelColumns(query.select, schemaObj, metaObj.component, metaObj.bundle)
            }
        }
    }
    return verifyIds({
        rows: setDefaults(levelLayout.rows),
        columns: setDefaults(levelLayout.columns)
    });
};

export { getLevelsLayout }
